import { supabase } from '../lib/supabase'
import type { CctBuscaAditivo, CctBuscaLista, CctUltimaBuscaAditivo } from '../types/cctBuscas'

export const cctBuscasService = {
  async getBuscasCcts(): Promise<CctBuscaLista[]> {
    const { data: cctBuscas, error: errorBuscas } = await supabase
      .from('cct_buscas')
      .select('*')
      .order('created_at', { ascending: false })

    if (errorBuscas) throw errorBuscas

    const { data: cctConvencoes, error: errorConvencoes } = await supabase
      .from('cct_convencoes')
      .select('*')

    if (errorConvencoes) throw errorConvencoes

    return (cctBuscas || []).map((busca: Record<string, unknown>) => {
      const convencao = (cctConvencoes || []).find(
        (conv: Record<string, unknown>) => conv.id === busca.cct_convencao_id,
      )
      return {
        id: busca.id as number,
        cct_convencao_id: (busca.cct_convencao_id as number | null) ?? null,
        resposta: busca.resposta,
        created_at: busca.created_at as string,
        cnpj_entidade_laboral: (convencao?.cnpj_entidade_laboral as string) ?? '',
        cnpj_entidade_patronal: (convencao?.cnpj_entidade_patronal as string) ?? '',
        status: (busca.status as string | null) ?? null,
        link_documento: (busca.link_documento as string | null) ?? null,
        link_download: (busca.link_download as string | null) ?? null,
        vigencia_inicio: (busca.vigencia_inicio as string | null) ?? null,
        vigencia_fim: (busca.vigencia_fim as string | null) ?? null,
        partes: (busca.partes as string | null) ?? null,
        codigo: (busca.codigo as string | null) ?? null,
        data_importacao: (busca.data_importacao as string | null) ?? null,
        id_scgp: (busca.id_scgp as number | string | null) ?? null,
        clausulas_especificas: (busca.clausulas_especificas as string | null) ?? null,
      }
    })
  },

  async getBuscasAditivos(): Promise<CctBuscaAditivo[]> {
    const { data, error } = await supabase
      .from('cct_buscas_aditivos')
      .select('*')
      .order('created_at', { ascending: false })

    if (error) throw error
    return (data ?? []) as CctBuscaAditivo[]
  },

  async getBuscasAditivosByConvencaoIds(convencaoIds: number[]): Promise<CctBuscaAditivo[]> {
    if (convencaoIds.length === 0) return []
    const { data, error } = await supabase
      .from('cct_buscas_aditivos')
      .select('*')
      .in('cct_convencao_id', convencaoIds)
      .order('created_at', { ascending: false })

    if (error) throw error
    return (data ?? []) as CctBuscaAditivo[]
  },

  async getUltimaBuscaAditivoPorConvencaoIds(convencaoIds: number[]): Promise<CctUltimaBuscaAditivo[]> {
    if (convencaoIds.length === 0) return []
    const { data, error } = await supabase.rpc('rpc_cct_ultima_busca_aditivo_por_convencao', {
      p_convencao_ids: convencaoIds
    })
    if (error) throw error
    return ((data as { cct_convencao_id?: number; status?: string | null; created_at?: string }[] | null) ?? [])
      .filter((row): row is { cct_convencao_id: number; status: string | null; created_at: string } => row?.cct_convencao_id != null)
      .map((row) => ({
        cct_convencao_id: Number(row.cct_convencao_id),
        status: row.status ?? null,
        created_at: row.created_at ?? ''
      }))
  },

  async atualizarStatusAditivoLocalizadaSemAlteracao(
    id: string,
    novoStatus: 'DISPONÍVEL PARA IMPORTAR' | 'SEM ALTERAÇÃO',
  ): Promise<number> {
    const { data, error } = await supabase.rpc('rpc_cct_atualizar_status_aditivo_localizada_sem_alteracao', {
      p_busca_id: id,
      p_novo_status: novoStatus,
    })
    if (error) throw error
    return Number(data ?? 0)
  },
}
